// src/views/MapPortal/PrintView/PrintLegend.tsx
import React from 'react';
import { Typography } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import '../PrintView.scss';

interface PrintLegendProps {
  layers: any[];
  onToggleLayer: (layerName: string) => void;
  scaleKm: string; // Add this
  scaleMi: string; // Add this
  isNorthingVisible: boolean;
  isScaleVisible: boolean;
}

const PrintLegend: React.FC<PrintLegendProps> = ({ layers, onToggleLayer, scaleKm, scaleMi, isNorthingVisible, isScaleVisible }) => {
  // Show layers that are set to be in the legend
  const legendLayers = layers.filter((l) => l.displayInLegend);

  if (legendLayers.length === 0) return null;

  const renderSymbol = (layer: any) => {
    // 1. Check for WMS
    if (layer.layerType === 'WMS') {
      return <GlobalOutlined className="print-legend__wms-icon" />;
    }

    // 2. Check for Raster (TIF/TIFF)
    if (layer.layerType === 'raster' || layer.uniqueLayerName.toLowerCase().includes('tif')) {
      return (
        <div className="raster-rgb-symbol">
          <div className="circle red" />
          <div className="circle green" />
          <div className="circle blue" />
        </div>
      );
    }

    // 3. Check for Quantity/Class Breaks (The dots row)
    if (layer.symbology === 'quantity' && layer.colorRamp) {
      return (
        <div className="quantity-symbols">
          <div className="symbols-row">
            {layer.colorRamp.colors.slice(0, 3).map((color: string, i: number) => (
              <svg key={i} className="print-legend__dot-svg" viewBox="0 0 10 10" aria-hidden="true">
                <circle cx="5" cy="5" r="4" fill={color} stroke="rgba(0,0,0,0.2)" strokeWidth="1" />
              </svg>
            ))}
          </div>
          <div className="quantity-labels">
            <span>{'>=0'}</span>
            <span>{'>0'}</span>
          </div>
        </div>
      );
    }

    // 4. Geometry specific symbols
    switch (layer.layerType) {
      case 'LineString':
      case 'MultiLineString': {
        // Fill is the line (colour + solid/dashed/dotted); Outline is an optional solid buffer.
        const lineColor = layer.fill || '#000';
        const hasBuffer = !!layer.outline && layer.outlineType !== 'none' && Number(layer.outlineWeight) > 0;
        const dash = layer.fillType === 'dashed' ? '6 3' : layer.fillType === 'dotted' ? '0.1 4' : undefined;
        return (
          <div className="line-symbol-container">
            <svg className="print-legend__line-svg" viewBox="0 0 30 14" aria-hidden="true">
              {hasBuffer && <line x1="0" y1="7" x2="30" y2="7" stroke={layer.outline} strokeWidth="5" />}
              <line x1="0" y1="7" x2="30" y2="7" stroke={lineColor} strokeWidth="3" strokeDasharray={dash} strokeLinecap={layer.fillType === 'dotted' ? 'round' : 'butt'} />
            </svg>
          </div>
        );
      }
      case 'Point':
      case 'MultiPoint':
        return (
          <svg className="print-legend__dot-svg" viewBox="0 0 10 10" aria-hidden="true">
            <circle cx="5" cy="5" r="4" fill={layer.fill || '#1890ff'} stroke="rgba(0,0,0,0.2)" strokeWidth="1" />
          </svg>
        );
      default:
        return (
          <svg className="print-legend__swatch-svg" viewBox="0 0 16 16" aria-hidden="true">
            <rect x="0.5" y="0.5" width="15" height="15" fill={layer.fill || '#1890ff'} stroke={layer.outline || '#000'} strokeWidth="1" />
          </svg>
        );
    }
  };

  return (
    <div className="print-legend-overlay">
      <div className="legend-scroll-area">
        <Typography.Text strong className="print-legend__heading">
          Layers
        </Typography.Text>

        {legendLayers.map((layer, idx) => (
          <div key={idx} className="legend-row clickable" onClick={() => onToggleLayer(layer.uniqueLayerName)}>
            <Typography.Text className="layer-title">{layer.layerTitle || layer.uniqueLayerName}</Typography.Text>
            <div className="symbol-container">{renderSymbol(layer)}</div>
          </div>
        ))}
      </div>

      <div className="legend-footer">
        <div className="">
          {isScaleVisible && (
            <div className="scale-container">
              <div className="scale-label top">{scaleKm}</div>
              <div className="scale-bar-graphic" />
              <div className="scale-label bottom">{scaleMi}</div>
            </div>
          )}
        </div>
        {isNorthingVisible && (
          <div className="northing-wrapper">
            <svg width="30" height="40" viewBox="0 0 30 40" fill="none">
              {/* The "N" Text */}
              <text x="50%" y="10" textAnchor="middle" fontSize="10" fontWeight="bold" fill="black">
                N
              </text>
              {/* Stylized Compass Needle */}
              <path d="M15 12 L22 35 L15 30 L8 35 Z" fill="black" />
              <path d="M15 12 L15 30 L8 35 Z" fill="#666" /> {/* Shading for 3D effect */}
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintLegend;
